import "../../handoffv3/handoff3/tokens.css";
import "../../handoffv2/scanning.css";
import "./viewfinder.css";
import type { ViewEndpoint } from "../protocol/events";
import { warn } from "../shared/debug";
import { isMobile } from "../shared/platform";
import {
  mountScanSession,
  sessionStorageIssue,
  subscribeSession,
  subscribeSessionHistory,
} from "../shared/scan-session";
import { mountBoot } from "./boot";
import { mountCapturePanel } from "./capture-panel";
import { armChime } from "./chime";
import { mountDebugPanel } from "./debug-panel";
import { element, make, mountToast } from "./dom";
import { mountFindLog } from "./find-log";
import { icon, mountIcons } from "./icons";
import { installContent } from "./install";
import { mountInteractionMonitor } from "./interaction";
import { panelManager } from "./panel";
import { mountPreview } from "./preview";
import { mountReview } from "./resolve";
import { mountScanHistory } from "./scan-history";
import { mountScanPanel } from "./scan-panel";
import { mountSettingsPanel } from "./settings-panel";
import { mountSessionSummary, mountStatus } from "./status";
import { mountTorch } from "./torch";
import { mountWakeLock } from "./wake-lock";
import { ViewControl } from "./view-control";

/** Composition owns lifecycle; components own presentation of the typed contract. */
export function mountView(endpoint: ViewEndpoint): void {
  mountIcons();
  const app = element("app");
  const boot = element("boot");
  const scan = element("scanScreen");
  const camera = element("camera");
  const start = element<HTMLButtonElement>("startBtn");
  const cameraOff = element("cameraOff");
  const cameraOffLabel = element("cameraOffLabel");
  const cameraRecovery = element("cameraRecovery");
  const cameraCheck = element<HTMLButtonElement>("cameraCheck");
  const cameraMessage = element("cameraMessage");
  const stop = element<HTMLButtonElement>("stopBtn");
  const bootScreen = mountBoot(boot, endpoint);
  element("allowCameraBtn").addEventListener("click", armChime, { capture: true });
  const say = mountToast(element("toast"));
  document.documentElement.classList.toggle("mobile-device", isMobile());
  const createPanel = panelManager(element("panelLayer"), element("workspace"));
  const historyPanel = createPanel("historyPanel", "Scans", "");
  const settingsPanel = createPanel("settingsPanel", "Settings", "");
  const installPanel = createPanel("installPanel", "Add to home screen", "");
  const debugPanel = createPanel("debugPanel", "Diagnostics", "");
  const capturePanel = createPanel("capturePanel", "Capture", "", "capture-panel");
  const openCapture = mountCapturePanel(capturePanel);
  const history = mountScanHistory(historyPanel.body, openCapture);
  historyPanel.onClose = history.close;
  mountSettingsPanel(settingsPanel.body);
  const settingsAssets = make("section", "panel-section");
  settingsAssets.append(make("h3", "section-label", "Scanner assets"));
  const backendLabel = make("p", "asset-backend");
  backendLabel.hidden = true;
  settingsAssets.append(backendLabel);
  const settingsWarnings = make("div", "notice");
  settingsWarnings.hidden = true;
  settingsAssets.append(settingsWarnings);
  settingsPanel.body.append(settingsAssets);
  const storageWarning = make("p", "notice");
  storageWarning.hidden = true;
  settingsAssets.append(storageWarning);
  const sessionStorageWarning = make("p", "notice");
  sessionStorageWarning.hidden = true;
  sessionStorageWarning.setAttribute("role", "status");
  element("scanPanel").querySelector(".session-alerts")?.append(sessionStorageWarning);
  const debug = mountDebugPanel(debugPanel.body);
  if (debug.liveAvailable) {
    const debugButton = make("button", "button button--ghost", "Open diagnostics");
    debugButton.type = "button";
    debugButton.onclick = () => {
      debugPanel.open();
      debug.showLive();
    };
    settingsPanel.body.append(debugButton);
  }
  const previousNotice = element("previousNotice");
  previousNotice.hidden = !debug.hasPrevious();
  element("previousOpen").onclick = () => {
    debugPanel.open();
    debug.showPrevious();
  };
  element("previousDismiss").onclick = () => {
    debug.clearPrevious();
    previousNotice.hidden = true;
  };
  debugPanel.onClose = () => {
    previousNotice.hidden = !debug.hasPrevious();
  };

  const control = new ViewControl();
  const preview = mountPreview(endpoint, control, camera, element<HTMLVideoElement>("cameraFeed"));
  mountScanSession(endpoint);
  const review = mountReview(element("panelLayer"), element("workspace"));
  const finds = mountFindLog(element("finds"), (id) => void review.open(id));
  const scanPanel = element("scanPanel");
  const stoppedStrip = element("stoppedStrip");
  const reviewButton = element<HTMLButtonElement>("reviewBtn");
  const scanPanelControl = mountScanPanel(
    scanPanel,
    element("sessionDrag"),
    finds.featuredHost,
    finds.list,
  );
  reviewButton.onclick = () => void review.open();
  mountSessionSummary(finds.summary);
  mountStatus(
    endpoint,
    element("sessionNotice"),
    element("status"),
    element<HTMLButtonElement>("crashReload"),
  );
  let ready = false;
  let scanning = false;
  let starting = false;
  let fatal = false;
  let cameraError = "";
  let permission: PermissionState | null = null;
  let focusCamera = false;
  /** The page went hidden with the camera open, and nobody asked to stop. */
  let resumeOnReturn = false;
  /** The attempt in flight is automatic — nobody tapped Start for it. */
  let resuming = false;
  /** The last session ended because the device took the camera away. */
  let cameraLost = false;

  const blocked = (): boolean =>
    permission === "denied" || !!cameraError || !navigator.mediaDevices?.getUserMedia;
  let renderedUnresolved: number | undefined;
  const updateSession = (): void => {
    const current = finds.summary();
    const hidden = (node: HTMLElement, value: boolean): void => {
      if (node.hidden !== value) node.hidden = value;
    };
    const text = (node: HTMLElement, value: string): void => {
      if (node.textContent !== value) node.textContent = value;
    };
    const issue = sessionStorageIssue();
    text(storageWarning, issue ?? "");
    hidden(storageWarning, !issue);
    hidden(sessionStorageWarning, !issue);
    text(sessionStorageWarning, "Session kept in this tab only. Browser storage is unavailable.");
    hidden(scanPanel, current.count === 0);
    hidden(element("scansBtn"), current.count === 0);
    hidden(stoppedStrip, scanning || current.count === 0);
    text(
      element("stoppedCount"),
      `stopped · ${current.count} ${current.count === 1 ? "card" : "cards"}`,
    );
    hidden(reviewButton, current.unresolved === 0);
    if (renderedUnresolved !== current.unresolved) {
      renderedUnresolved = current.unresolved;
      reviewButton.replaceChildren(
        document.createTextNode(`${current.unresolved} unresolved`),
        make("span", "", "→"),
      );
    }
    hidden(stop, !scanning && (current.count === 0 || blocked()));
    if (stop.disabled !== starting) stop.disabled = starting;
    const action = scanning ? "stop" : "start";
    if (stop.dataset.action !== action) {
      stop.dataset.action = action;
      stop.replaceChildren(icon(scanning ? "stop" : "play"));
    }
    const label = scanning ? "Stop scanning" : "Start scanning";
    if (stop.getAttribute("aria-label") !== label) stop.setAttribute("aria-label", label);
    scanPanelControl.refresh();
  };

  const showIdleCamera = (): void => {
    bootScreen.hide(false);
    scan.hidden = false;
    const isBlocked = blocked();
    app.dataset.state = isBlocked ? "blocked" : finds.count() ? "stopped" : "idle";
    cameraOff.hidden = finds.count() > 0 && !isBlocked;
    start.hidden = isBlocked;
    start.disabled = !ready || starting;
    start.removeAttribute("aria-busy");
    cameraOffLabel.textContent = isBlocked
      ? "camera blocked"
      : cameraLost
        ? "camera closed"
        : "no session yet";
    cameraMessage.textContent = isBlocked
      ? "The scanner is loaded and ready — it just has no camera to look through."
      : cameraLost
        ? "Your device closed the camera — a call, another app, or the browser's own camera control. Start again once it is free."
        : "Hold a card in front of the camera. Every find lands here as its own row.";
    cameraRecovery.hidden = !isBlocked;
    cameraCheck.hidden = !isBlocked;
    cameraCheck.disabled = starting;
    updateSession();
  };

  // Permission is advisory UI state. Only the scanner requests the actual stream.
  void navigator.permissions
    ?.query({ name: "camera" as PermissionName })
    .then((status) => {
      const update = (): void => {
        permission = status.state;
        if (!ready || scanning || starting || (!boot.hidden && permission !== "denied")) return;
        showIdleCamera();
      };
      update();
      status.addEventListener("change", update);
    })
    .catch(() => {});

  /**
   * Open the camera. `gesture` says whether this is a tap: the chime is gesture-gated
   * on iOS and is armed here, on the tap's stack, before the scanner opens its camera.
   */
  const openCamera = (gesture: boolean): void => {
    if (!ready || scanning || starting || fatal) return;
    if (gesture) {
      // Keep this on the tap's stack, before the scanner opens its camera.
      armChime();
      focusCamera =
        document.activeElement === start ||
        document.activeElement === stop ||
        document.activeElement === cameraCheck;
    }
    resuming = !gesture;
    starting = true;
    cameraError = "";
    cameraLost = false;
    app.dataset.state = "starting";
    start.disabled = true;
    start.setAttribute("aria-busy", "true");
    cameraOffLabel.textContent = "opening camera";
    cameraMessage.textContent = "Allow camera access if asked.";
    cameraCheck.disabled = true;
    stop.disabled = true;
    endpoint.emit("start");
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (orientation: string) => Promise<void>;
    };
    void orientation?.lock?.("portrait-primary").catch(() => {});
  };
  const startCamera = (): void => openCamera(true);
  /**
   * The page went hidden with the camera open — the phone locked, a call took the
   * screen, an app switch. Stop, as before, and remember that nobody asked to: the
   * session comes back by itself when the page does. Only a Stop tap ends one for good.
   */
  const pauseForHidden = (): void => {
    resumeOnReturn = true;
    endpoint.emit("stop");
  };
  /**
   * Bring the camera back without a tap. Where the permission is remembered this is
   * silent; where it is per visit — iOS Safari on its default setting — the prompt is
   * the one a tap would have raised anyway.
   */
  const resumeCamera = (): void => {
    if (blocked()) return;
    openCamera(false);
  };
  installContent(installPanel.body, () => {
    installPanel.close();
  });
  const showInstall = (): void => {
    const proceed = installPanel.body.querySelector<HTMLButtonElement>(".button--primary");
    if (proceed) proceed.textContent = "Continue in browser";
    installPanel.open();
  };
  start.onclick = () => {
    if (fatal) {
      location.reload();
      return;
    }
    startCamera();
  };
  cameraCheck.onclick = startCamera;
  const installButton = make("button", "button button--ghost", "Add to home screen");
  installButton.id = "installLink";
  installButton.type = "button";
  installButton.onclick = showInstall;
  settingsPanel.body.append(installButton);
  stop.onclick = () => {
    if (scanning) {
      resumeOnReturn = false;
      stop.disabled = true;
      endpoint.emit("stop");
    } else startCamera();
  };
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-open]")) {
    button.onclick = () => {
      if (button.dataset.open === "history") {
        historyPanel.open();
        void history.open();
      } else {
        settingsPanel.open();
      }
    };
  }
  endpoint.on("boot:ready", ({ backend, warnings }) => {
    ready = true;
    fatal = false;
    backendLabel.textContent = backend.toUpperCase();
    backendLabel.hidden = false;
    for (const target of [element("bootWarnings"), settingsWarnings]) {
      target.replaceChildren(...warnings.map((message) => make("p", "", message)));
      target.hidden = !warnings.length;
    }
    if (blocked()) showIdleCamera();
    if (!historyPanel.element.hidden) void history.refresh();
  });
  endpoint.on("boot:error", () => {
    fatal = true;
    ready = false;
    app.dataset.state = "error";
  });
  endpoint.on("boot:retrying", () => {
    fatal = false;
    app.dataset.state = "loading";
  });
  endpoint.on("camera:opened", ({ sensorWidth, sensorHeight }) => {
    cameraError = "";
    permission = "granted";
    scan.hidden = false;
    if (!fatal) app.dataset.state = "boot-camera";
    cameraOff.hidden = true;
    preview.resizeSensor({ width: sensorWidth, height: sensorHeight });
  });
  endpoint.on("session:started", ({ sensorWidth, sensorHeight }) => {
    starting = false;
    scanning = true;
    resuming = false;
    start.hidden = true;
    cameraOff.hidden = true;
    start.removeAttribute("aria-busy");
    stop.hidden = false;
    stop.disabled = false;
    stop.setAttribute("aria-label", "Stop scanning");
    bootScreen.hide();
    scan.hidden = false;
    app.dataset.state = "scanning";
    // Layout is visible before measuring; the first detector frame waits for this emit.
    preview.start({ width: sensorWidth, height: sensorHeight });
    updateSession();
    if (document.hidden) pauseForHidden();
    else if (focusCamera && !stop.closest("[inert]")) stop.focus({ preventScroll: true });
    focusCamera = false;
  });
  endpoint.on("session:stopped", ({ reason }) => {
    const restoreFocus =
      document.activeElement === stop ||
      document.activeElement === element("torchBtn") ||
      camera.contains(document.activeElement);
    starting = false;
    scanning = false;
    resuming = false;
    cameraLost = reason === "camera-lost";
    preview.stop();
    if (ready) showIdleCamera();
    // With finds on screen the camera-off copy is hidden, so say it where it shows.
    if (cameraLost && finds.count() > 0)
      say("Your device closed the camera. Tap Start to continue.");
    if (restoreFocus && !document.hidden && !start.closest("[inert]"))
      (stop.hidden ? start : stop).focus({ preventScroll: true });
  });
  endpoint.on("camera:failed", ({ message }) => {
    starting = false;
    const automatic = resuming;
    resuming = false;
    if (automatic) {
      // An attempt nobody asked for must not declare the camera blocked — the device
      // may simply not have handed it back yet. Leave Start in place and say so.
      if (ready) showIdleCamera();
      say(`The camera did not come back: ${message}. Tap Start to try again.`);
      return;
    }
    cameraError = message;
    if (ready) showIdleCamera();
    if (focusCamera && !document.hidden && !start.closest("[inert]"))
      cameraCheck.focus({ preventScroll: true });
    focusCamera = false;
  });
  endpoint.on("sensor:resized", ({ sensorWidth, sensorHeight }) => {
    preview.resizeSensor({ width: sensorWidth, height: sensorHeight });
  });
  endpoint.on("status", ({ kind, text }) => {
    if (kind === "crashed") fatal = true;
    // The session notice lives in the finds panel, which an empty session does not show.
    if (kind === "paused" && scanPanel.hidden) say(text);
  });
  subscribeSession((change) => {
    // Late thumbnails/JPEGs do not change controls, counts or camera state.
    if (!change.mediaOnly) {
      if (boot.hidden && !scanning && !starting) showIdleCamera();
      else updateSession();
    }
  });
  // Saving the JPEG alone is not enough: a choice/discard made during encoding must
  // also be committed before open history reloads. Media updates need no early refresh.
  subscribeSessionHistory(() => {
    if (!historyPanel.element.hidden) void history.refresh();
  });
  updateSession();
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      if (scanning || starting) pauseForHidden();
      return;
    }
    if (!resumeOnReturn) return;
    resumeOnReturn = false;
    resumeCamera();
  });
  const resize = (): void => {
    preview.reframe();
  };
  window.addEventListener("resize", resize);
  window.visualViewport?.addEventListener("resize", resize);
  new ResizeObserver(resize).observe(camera);
  mountTorch(element<HTMLButtonElement>("torchBtn"), endpoint, say);
  mountWakeLock(endpoint);
  mountInteractionMonitor(
    endpoint,
    [historyPanel, settingsPanel, installPanel, debugPanel, capturePanel].map(
      (panel) => panel.element,
    ),
  );
  if ("serviceWorker" in navigator && window.isSecureContext) {
    const register = (): void => {
      void navigator.serviceWorker
        .register(new URL("sw.js", document.baseURI))
        .catch((error) => warn("boot", "PWA service worker registration failed", error));
    };
    if (document.readyState === "complete") register();
    else window.addEventListener("load", register, { once: true });
  }
}
